/**
 * DCP (DNG Camera Profile) parser — Phase 3.B-color P4.
 *
 * DCPs are TIFF-EP containers with a fixed magic header (II*\0 or MM\0*)
 * and Adobe-defined private tags in the 50000+ range. They carry the
 * per-camera color science needed to map cameraRGB → display RGB
 * accurately, instead of libraw's generic sRGB matrix.
 *
 * Reference: Adobe DNG Specification 1.7 (October 2023), section 6.4.
 * Public spec (no GPL code copy from RawTherapee or darktable).
 *
 * v1 ship — extract only the matrices + illuminant codes:
 *   50721 ColorMatrix1               SRATIONAL[9]
 *   50722 ColorMatrix2               SRATIONAL[9]    (optional)
 *   50964 ForwardMatrix1             SRATIONAL[9]    (optional, preferred)
 *   50965 ForwardMatrix2             SRATIONAL[9]    (optional)
 *   50778 CalibrationIlluminant1     SHORT[1]
 *   50779 CalibrationIlluminant2     SHORT[1]        (optional)
 *   50936 ProfileName                ASCII
 *
 * v1 detects but does NOT apply:
 *   50937 ProfileHueSatMapDims       LONG[3]   → hasHueSatMap=true
 *   50938 ProfileHueSatMapData1      FLOAT[N]
 *   50982 ProfileLookTableData       FLOAT[N]  → hasLookTable=true
 *
 * The HueSatMap LUT chain + LookTable creative-look are deferred to
 * Phase 3.B.1 (proper hue-sat 3D LUT in HSV space, computationally
 * comparable to a 33×33×33 Lab grade).
 *
 * Parser uses the existing vendored UTIF.js (TIFF reader; MIT). UTIF
 * exposes IFD entries as `ifd['t'+tagId]` arrays. SRATIONAL pairs
 * arrive as `[numerator, denominator]`; we collapse into Float32.
 *
 * @module src/pipeline/dcp/loader
 */

let utifLoadPromise = null;

/**
 * Lazy-load UTIF.js (already vendored for TIFF decode). Returns the
 * UTIF namespace once available on `window.UTIF`. Same loader pattern
 * as src/pipeline/decoders/tiff.js — UTIF is CommonJS so we go through
 * a `<script>` injection instead of dynamic ESM import.
 */
function loadUTIF() {
    if (typeof window !== 'undefined' && window.UTIF) return Promise.resolve(window.UTIF);
    if (utifLoadPromise) return utifLoadPromise;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.reject(new Error('DCP parser requires a browser environment'));
    }
    utifLoadPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = '/vendor/utif/UTIF.js';
        s.async = true;
        s.onload = () => {
            if (window.UTIF) resolve(window.UTIF);
            else reject(new Error('UTIF.js loaded but window.UTIF is undefined'));
        };
        s.onerror = () => reject(new Error('Failed to load /vendor/utif/UTIF.js'));
        document.head.appendChild(s);
    });
    return utifLoadPromise;
}

/* DCP TIFF tag IDs (Adobe DNG 1.7 § 6.4). Values cross-checked against
 * Adobe's public DNG SDK header (`dng_tag_codes.h`) — the spec PDF is
 * truthful but the SDK is the canonical numeric table. */
const TAG = Object.freeze({
    PROFILE_NAME:                 50936,    /* 0xC6F8 — was 50831 in earlier DNG drafts; current spec uses 50936 */
    CALIBRATION_ILLUMINANT_1:     50778,
    CALIBRATION_ILLUMINANT_2:     50779,
    COLOR_MATRIX_1:               50721,
    COLOR_MATRIX_2:               50722,
    FORWARD_MATRIX_1:             50964,
    FORWARD_MATRIX_2:             50965,
    PROFILE_HUE_SAT_MAP_DIMS:     50937,
    PROFILE_HUE_SAT_MAP_DATA_1:   50938,
    PROFILE_HUE_SAT_MAP_DATA_2:   50939,
    PROFILE_LOOK_TABLE_DIMS:      50981,
    PROFILE_LOOK_TABLE_DATA:      50982,
});

/**
 * Convert an SRATIONAL TIFF array — UTIF.js delivers it as an array of
 * `[numerator, denominator]` pairs — into a flat Float32Array. Returns
 * null if the input is malformed or the wrong length.
 *
 * @param {Array} arr        UTIF SRATIONAL array (length 9, entries [num, den])
 * @param {number} expectN   expected element count (9 for 3×3 matrices)
 * @returns {Float32Array|null}
 */
function rationalToFloat(arr, expectN) {
    if (!Array.isArray(arr) || arr.length !== expectN) return null;
    const out = new Float32Array(expectN);
    for (let i = 0; i < expectN; i++) {
        const pair = arr[i];
        if (!Array.isArray(pair) || pair.length !== 2) return null;
        const den = pair[1];
        if (!Number.isFinite(den) || den === 0) return null;
        const v = pair[0] / den;
        if (!Number.isFinite(v)) return null;
        out[i] = v;
    }
    return out;
}

/**
 * Pull the first scalar from an IFD short/long entry.
 */
function firstUint(ifd, tagId) {
    const v = ifd['t' + tagId];
    if (!v || v.length === 0) return null;
    const n = v[0] | 0;
    return Number.isFinite(n) ? n : null;
}

/**
 * Pull a string out of an ASCII tag. UTIF stores ASCII as either a
 * single-element array containing the string, or a Uint8Array (when
 * the high-bit byte set). Decode both shapes.
 */
function asciiTag(ifd, tagId) {
    const v = ifd['t' + tagId];
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string') {
        return v[0];
    }
    if (v instanceof Uint8Array) {
        let s = '';
        for (let i = 0; i < v.length && v[i] !== 0; i++) s += String.fromCharCode(v[i]);
        return s;
    }
    return '';
}

/**
 * Parse a DCP blob or ArrayBuffer. Returns null on any structural
 * failure (not a TIFF, missing required tags, malformed matrices) so
 * the caller can cleanly fall back to the legacy ingest path.
 *
 * NEVER throws: the RAW decode path is hot and a corrupt user-side
 * DCP shouldn't surface as a JS exception.
 *
 * @param {Blob|ArrayBuffer|Uint8Array} input
 * @returns {Promise<{
 *   profileName: string,
 *   calibrationIlluminant1: number,
 *   calibrationIlluminant2: number,
 *   colorMatrix1: Float32Array,
 *   colorMatrix2: Float32Array|null,
 *   forwardMatrix1: Float32Array|null,
 *   forwardMatrix2: Float32Array|null,
 *   hasHueSatMap: boolean,
 *   hasLookTable: boolean,
 * }|null>}
 */
export async function parseDCP(input) {
    if (!input) return null;

    /* Accept Blob | ArrayBuffer | Uint8Array. Normalise to Uint8Array. */
    let bytes;
    try {
        if (input instanceof Uint8Array) {
            bytes = input;
        } else if (typeof ArrayBuffer !== 'undefined' && input instanceof ArrayBuffer) {
            bytes = new Uint8Array(input);
        } else if (input && typeof input.arrayBuffer === 'function') {
            const ab = await input.arrayBuffer();
            bytes = new Uint8Array(ab);
        } else {
            return null;
        }
    } catch {
        return null;
    }

    /* TIFF magic check — DCPs are always II*\0 (LE) per Adobe spec
     * (every Adobe-shipped DCP is little-endian; the spec allows BE
     * but tooling never emits it). Permit either to be lenient. */
    if (bytes.length < 16) return null;
    const isLE = bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2A && bytes[3] === 0x00;
    const isBE = bytes[0] === 0x4D && bytes[1] === 0x4D && bytes[2] === 0x00 && bytes[3] === 0x2A;
    if (!isLE && !isBE) return null;

    /* loadUTIF() can reject (CSP-blocked vendor fetch, network failure,
     * non-browser environment). Treat any failure as "DCP unavailable"
     * and return null so the RAW decode path falls back to the legacy
     * ingest — never throws to the caller. */
    let UTIF;
    try {
        UTIF = await loadUTIF();
    } catch {
        return null;
    }
    if (!UTIF || typeof UTIF.decode !== 'function') return null;

    let ifds;
    try {
        /* Skip MakerNote parsing — DCPs don't have one and parseMN:true
         * pulls in heuristic code paths we don't need. */
        ifds = UTIF.decode(bytes, { parseMN: false, debug: false });
    } catch {
        return null;
    }
    if (!Array.isArray(ifds) || ifds.length === 0) return null;

    /* DCPs put all profile tags in IFD 0. */
    const ifd = ifds[0];
    if (!ifd || typeof ifd !== 'object') return null;

    /* ColorMatrix1 is the only required matrix per DNG spec. */
    const cm1 = rationalToFloat(ifd['t' + TAG.COLOR_MATRIX_1], 9);
    if (!cm1) return null;

    const cm2 = rationalToFloat(ifd['t' + TAG.COLOR_MATRIX_2], 9);
    const fm1 = rationalToFloat(ifd['t' + TAG.FORWARD_MATRIX_1], 9);
    const fm2 = rationalToFloat(ifd['t' + TAG.FORWARD_MATRIX_2], 9);

    const illum1 = firstUint(ifd, TAG.CALIBRATION_ILLUMINANT_1) ?? 21; /* default D65 */
    const illum2 = firstUint(ifd, TAG.CALIBRATION_ILLUMINANT_2) ?? 0;  /* 0 = absent */

    return {
        profileName:            asciiTag(ifd, TAG.PROFILE_NAME),
        calibrationIlluminant1: illum1,
        calibrationIlluminant2: illum2,
        colorMatrix1:           cm1,
        colorMatrix2:           cm2,
        forwardMatrix1:         fm1,
        forwardMatrix2:         fm2,
        hasHueSatMap:           Boolean(ifd['t' + TAG.PROFILE_HUE_SAT_MAP_DIMS]),
        hasLookTable:           Boolean(ifd['t' + TAG.PROFILE_LOOK_TABLE_DATA]
                                     || ifd['t' + TAG.PROFILE_LOOK_TABLE_DIMS]),
    };
}

/**
 * Internal export for testing — lets test scripts construct a synthetic
 * minimal DCP and verify round-trip parsing. NOT part of the public
 * API surface used by callers.
 */
export const __TAGS = TAG;
